/**
 * API Tokens Tab - R2: PAT Management UI
 *
 * Self-service Personal Access Token management for Claude Code / MCP integration.
 * - Create tokens with name, expiration, tenant, scopes
 * - Full token shown once on creation (never again)
 * - List: prefix, name, tenant, scopes, last_used_at, expires_at
 * - Revoke with confirmation; optimistic update via setQueriesData
 * - Mobile-responsive at 375px
 */
import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Key, Plus, Trash2, Copy, Check, ExternalLink, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/AuthProvider';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  tenant_id: string;
  tenant_name?: string;
  scopes: string[];
  last_used_at: string | null;
  expires_at: string;
  created_at: string;
  user_id?: string;
  user_name?: string;
  // R2: connector linkage — surfaced from the backend so a self-serve connector
  // entry can be distinguished from an ordinary desktop PAT (and revoked as one).
  is_connector_pat?: boolean;
  alias?: string | null;
  owner_uid?: string | null;
}

// R2: response of POST /api/tokens/connector — the self-serve connector mint.
interface ConnectorMintResponse {
  token_id: string;
  token: string;
  alias: string;
  tenant_id: string;
  scopes: string[];
  is_connector_pat: boolean;
  expires_at: string | null;
  created_at: string | null;
  // The claude.ai WEB connector URL (the `/mcp-oauth/mcp/` form). May be null if
  // the OAuth base URL is not configured for this deploy.
  connector_url: string | null;
}

interface TokenListResponse {
  tokens: ApiToken[];
}

interface CreateTokenRequest {
  name: string;
  expires_days: number;
  tenant_id: string;
  scopes: string[];
}

interface CreateTokenResponse {
  token_id: string;
  prefix: string;
  name: string;
  tenant_id: string;
  scopes: string[];
  token: string;
  scopes_frozen_permissions: string[];
}

// ─── Scope definitions ────────────────────────────────────────────────────────

// W1b R4 (AC3/S6): scopes are no longer a hardcoded array. They are fetched from
// GET /api/tokens/available-scopes, which returns ONLY the scopes the caller may
// actually mint (server-filtered by role/tier — the UI is not the boundary, MF2).
// Each record carries {value,label,description,domain,min_role,tier} so the picker
// can group by domain and render labels/descriptions without re-authoring them.
interface AvailableScope {
  value: string;
  label: string;
  description: string;
  domain: string;
  min_role: string;
  tier: string;
}

const EXPIRATION_OPTIONS = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days (recommended)' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year (maximum)' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function isExpiringSoon(iso: string): boolean {
  const d = new Date(iso);
  const daysLeft = (d.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return daysLeft < 14;
}

// R5 AC2: connector expiry is no longer silent. An expired connector PAT gets an
// explicit "Expired" state (not just an amber past-date badge); a connection
// expiring within ~3 days gets an in-app warning so the user can re-enable before
// it silently stops working (claude.ai caches on a URL that never changes).
const CONNECTOR_EXPIRY_WARNING_DAYS = 3;

function isExpired(iso: string | null): boolean {
  if (!iso) return false;
  return new Date(iso).getTime() < Date.now();
}

function isExpiringWithinDays(iso: string | null, days: number): boolean {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  const now = Date.now();
  return t >= now && (t - now) / (1000 * 60 * 60 * 24) < days;
}

// Group the flat available-scopes list by domain, preserving a stable order.
function groupScopesByDomain(scopes: AvailableScope[]): [string, AvailableScope[]][] {
  const groups = new Map<string, AvailableScope[]>();
  for (const s of scopes) {
    const list = groups.get(s.domain) ?? [];
    list.push(s);
    groups.set(s.domain, list);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => a.localeCompare(b));
}

// The "Read-only" preset: every offered :read scope. This is the DEFAULT selection
// (AC3 — not a single hardcoded scope). Falls back to empty if no reads are
// offered (a no-permission caller).
function readOnlyPreset(scopes: AvailableScope[]): string[] {
  return scopes.filter((s) => s.value.endsWith(':read')).map((s) => s.value);
}

function domainLabel(domain: string): string {
  return domain
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

// Tier of a minted token, derived from its granted scopes (AC4). A token holding
// any admin-namespace scope is super-admin tier; otherwise tenant tier.
function tierForScopes(scopes: string[]): 'super-admin' | 'tenant' {
  return scopes.some((s) => s.startsWith('admin:')) ? 'super-admin' : 'tenant';
}

// R2 connector expiry tiers — MIRROR the backend (auth/connector_mint.py):
// ANY :delete or action scope -> short (<=14d) ceiling; read/:write-only -> 90d.
// Enumerated action scopes are irreversible / third-party-visible so they earn
// the short window like :delete. Kept in sync with ACTION_SCOPE_STRINGS server-side.
const CONNECTOR_DELETE_SCOPE_MAX_EXPIRY_DAYS = 14;
const CONNECTOR_DEFAULT_EXPIRY_DAYS = 90;
const ACTION_SCOPE_SUFFIXES = [':send', ':submit', ':approve', ':sync', ':publish'];

// R2 AC7: a scope that earns the SHORT connector expiry — any :delete or action.
function isDeleteOrActionScope(scope: string): boolean {
  const s = (scope || '').trim();
  if (s.endsWith(':delete')) return true;
  return ACTION_SCOPE_SUFFIXES.some((suffix) => s.endsWith(suffix));
}

// R2 AC7: the expiry the connector PAT will receive for the given selection.
function connectorExpiryDaysForScopes(scopes: string[]): number {
  return scopes.some(isDeleteOrActionScope)
    ? CONNECTOR_DELETE_SCOPE_MAX_EXPIRY_DAYS
    : CONNECTOR_DEFAULT_EXPIRY_DAYS;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TokenRow({
  token,
  isAdmin,
  onRevoke,
}: {
  token: ApiToken;
  isAdmin: boolean;
  onRevoke: (token: ApiToken) => void;
}) {
  const expiring = isExpiringSoon(token.expires_at);
  return (
    <div
      className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
      data-testid={`token-row-${token.id}`}
    >
      {/* Token identity */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-sm text-gray-500 shrink-0">{token.prefix}…</span>
          <span className="font-medium text-gray-900 truncate">{token.name}</span>
          {isAdmin && token.user_name && (
            <span className="text-xs text-gray-400">({token.user_name})</span>
          )}
        </div>
        {/* Tenant + scopes */}
        <div className="mt-1 flex flex-wrap gap-1 items-center">
          {token.tenant_name && (
            <span className="text-xs text-gray-500 mr-1">{token.tenant_name}</span>
          )}
          {token.scopes.map((s) => (
            <Badge key={s} variant="secondary" className="text-xs px-1.5 py-0">
              {s}
            </Badge>
          ))}
        </div>
      </div>

      {/* Metadata */}
      <div className="flex flex-col sm:items-end text-xs text-gray-400 gap-0.5 shrink-0">
        <span>Last used: {formatDate(token.last_used_at)}</span>
        <span className={expiring ? 'text-amber-600 font-medium' : ''}>
          Expires: {formatDate(token.expires_at)}
          {expiring && ' ⚠'}
        </span>
      </div>

      {/* Actions */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => onRevoke(token)}
        data-testid={`revoke-token-${token.id}`}
        className="text-gray-400 hover:text-red-600 shrink-0"
        aria-label={`Revoke token ${token.name}`}
      >
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );
}

// Copy button with transient "copied" state
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handle}
      data-testid="copy-token-button"
      className="shrink-0"
    >
      {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
      {copied ? 'Copied!' : 'Copy'}
    </Button>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ApiTokensTab() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = user?.role === 'admin';

  // ── Dialog state ──
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [revealedScopes, setRevealedScopes] = useState<string[]>([]);
  const [tokenToRevoke, setTokenToRevoke] = useState<ApiToken | null>(null);
  const [isRevokeOpen, setIsRevokeOpen] = useState(false);

  // ── Create form state ──
  const [tokenName, setTokenName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('90');
  const [selectedTenantId, setSelectedTenantId] = useState(user?.tenant_id ?? '');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);

  // ── Connector (self-serve claude.ai web connector) state — R2 ──
  const [isConnectorOpen, setIsConnectorOpen] = useState(false);
  const [connectorScopes, setConnectorScopes] = useState<string[]>([]);
  const [revealedConnectorUrl, setRevealedConnectorUrl] = useState<string | null>(null);
  const [connectorToRevoke, setConnectorToRevoke] = useState<ApiToken | null>(null);
  const [isConnectorRevokeOpen, setIsConnectorRevokeOpen] = useState(false);
  // R5 AC1: when a re-mint CHANGES the scope set, claude.ai's cached tool catalog
  // (keyed on a URL that never changes) is stale — the user must disconnect +
  // re-add the connector in claude.ai for it to take effect. Track that across the
  // mint so the reveal dialog shows the prominent re-add callout.
  const [revealReAddRequired, setRevealReAddRequired] = useState(false);
  const priorConnectorScopesRef = useRef<string[] | null>(null);

  const availableTenants = user?.available_tenants ?? [];
  const isMultiTenant = availableTenants.length > 1;

  // ── R2 AC1: capability + tenant gate. The affordance renders ONLY when BOTH
  //    the per-user `ai_connector` module AND the tenant `mcp_access` flag are
  //    true — the SAME two-gate AND the backend mint endpoint enforces. NOTE: we
  //    do NOT use auth.can('ai_connector') here because `can` returns true for
  //    any admin (admin bypass); the capability is an EXPLICIT module grant with
  //    no admin bypass (matches R1's auth.permissions.require_feature, which reads
  //    user.modules directly). Absent => the whole connector UI is not rendered.
  const hasAiConnectorCapability = user?.modules?.ai_connector === true;
  const tenantHasMcpAccess = user?.tenant?.mcp_access === true;
  const connectorAffordanceVisible = hasAiConnectorCapability && tenantHasMcpAccess;

  // ── Available-scopes query (AC2/AC3): the server-filtered picker source ──
  const { data: availableScopes = [] } = useQuery({
    queryKey: ['available-scopes', user?.tenant_id],
    queryFn: async () => {
      const res = await api.get('/api/tokens/available-scopes');
      return (Array.isArray(res) ? res : []) as AvailableScope[];
    },
  });
  const scopeGroups = groupScopesByDomain(availableScopes);
  const preset = readOnlyPreset(availableScopes);

  // ── Query ──
  const { data, isLoading } = useQuery({
    queryKey: ['tokens', user?.tenant_id],
    queryFn: async () => {
      const tenantId = user?.tenant_id;
      if (!tenantId) return { tokens: [] as ApiToken[] };
      const res = await api.get(`/api/tokens?tenant_id=${encodeURIComponent(tenantId)}`);
      // Backend returns array of TokenResponse (with token_id, not id)
      const raw = Array.isArray(res) ? res : [];
      const tokens: ApiToken[] = raw.map((t: any) => ({ ...t, id: t.token_id }));
      return { tokens };
    },
  });
  const tokens = data?.tokens ?? [];

  // ── Create mutation ──
  const createToken = useMutation({
    mutationFn: async (payload: CreateTokenRequest) => {
      const res = await api.post('/api/tokens', payload);
      return res as CreateTokenResponse;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tokens', user?.tenant_id] });
      setIsCreateOpen(false);
      resetCreateForm();
      setRevealedScopes(res.scopes ?? []);
      setRevealedToken(res.token);
    },
    onError: (err: any) => {
      const msg = err?.message ?? 'Failed to create token';
      if (err?.status === 429) {
        toast.error('Maximum of 10 active tokens reached. Revoke an existing token first.');
      } else {
        toast.error(msg);
      }
    },
  });

  // ── Revoke mutation (optimistic) ──
  const revokeToken = useMutation({
    mutationFn: ({ id, tenant_id }: { id: string; tenant_id: string }) =>
      api.delete(`/api/tokens/${id}?tenant_id=${encodeURIComponent(tenant_id)}`),
    onMutate: async ({ id }: { id: string; tenant_id: string }) => {
      await queryClient.cancelQueries({ queryKey: ['tokens', user?.tenant_id] });
      const previous = queryClient.getQueryData<TokenListResponse>(['tokens', user?.tenant_id]);
      queryClient.setQueriesData<TokenListResponse>(
        { queryKey: ['tokens', user?.tenant_id] },
        (old) =>
          old
            ? { ...old, tokens: old.tokens.filter((t) => t.id !== id) }
            : old
      );
      return { previous };
    },
    onError: (_err, _vars, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(['tokens', user?.tenant_id], context.previous);
      }
      toast.error('Failed to revoke token');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens', user?.tenant_id] });
    },
    onSuccess: () => {
      toast.success('Token revoked');
      setIsRevokeOpen(false);
      setTokenToRevoke(null);
    },
  });

  // ── Connector mint mutation (R2 AC3) ──
  // Calls the R1 endpoint POST /api/tokens/connector (NOT the desktop POST
  // /api/tokens). On success it reveals the WEB connector URL for claude.ai and a
  // one-time confirmation that the wallet entry is registered.
  const mintConnector = useMutation({
    mutationFn: async (payload: { tenant_id: string; scopes: string[] }) => {
      const res = await api.post('/api/tokens/connector', payload);
      return res as ConnectorMintResponse;
    },
    onSuccess: (res) => {
      // Re-enable REPLACES (R2 AC8): the backend revokes the prior entry + writes
      // one row per (uid, tenant). Refetch so the list shows exactly one entry.
      queryClient.invalidateQueries({ queryKey: ['tokens', user?.tenant_id] });
      // R5 AC1: did this re-mint CHANGE the scope set vs the prior connector? If
      // so, claude.ai's cached tool catalog is stale and the user must disconnect
      // + re-add in claude.ai. A first-time enable (no prior) does not need it —
      // there is nothing cached in claude.ai yet.
      const prior = priorConnectorScopesRef.current;
      const next = [...(res.scopes ?? [])].sort();
      const scopeChanged =
        prior !== null && JSON.stringify(prior) !== JSON.stringify(next);
      setRevealReAddRequired(scopeChanged);
      priorConnectorScopesRef.current = null;
      setIsConnectorOpen(false);
      setRevealedConnectorUrl(res.connector_url ?? '');
    },
    onError: (err: any) => {
      const msg = err?.message ?? 'Failed to enable the Claude connector';
      if (err?.status === 429) {
        toast.error('Maximum of 10 active tokens reached. Revoke an existing token first.');
      } else {
        toast.error(msg);
      }
    },
  });

  // ── Connector revoke mutation (R2 AC4) ──
  // Calls the R1 connector-revoke path DELETE /api/tokens/connector/{id}, which
  // revokes the PAT AND deletes the OAuth wallet row (the plain DELETE /{id} does
  // NOT remove the wallet row). Optimistic, mirroring the desktop revoke.
  const revokeConnector = useMutation({
    mutationFn: ({ id, tenant_id, owner_uid }: { id: string; tenant_id: string; owner_uid?: string | null }) => {
      const ownerQs = owner_uid ? `&owner_uid=${encodeURIComponent(owner_uid)}` : '';
      return api.delete(
        `/api/tokens/connector/${id}?tenant_id=${encodeURIComponent(tenant_id)}${ownerQs}`
      );
    },
    onMutate: async ({ id }: { id: string; tenant_id: string; owner_uid?: string | null }) => {
      await queryClient.cancelQueries({ queryKey: ['tokens', user?.tenant_id] });
      const previous = queryClient.getQueryData<TokenListResponse>(['tokens', user?.tenant_id]);
      queryClient.setQueriesData<TokenListResponse>(
        { queryKey: ['tokens', user?.tenant_id] },
        (old) => (old ? { ...old, tokens: old.tokens.filter((t) => t.id !== id) } : old)
      );
      return { previous };
    },
    onError: (err: any, _vars, context: any) => {
      if (context?.previous) {
        queryClient.setQueryData(['tokens', user?.tenant_id], context.previous);
      }
      // R5 AC8: surface the backend's detail.message (api.delete throws an Error
      // whose message is extractErrorMessage(detail) — prefers detail.message)
      // instead of a generic string, so a guarded-cascade / revoke failure tells
      // the user what actually happened.
      toast.error(err?.message ?? 'Failed to revoke the connector');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tokens', user?.tenant_id] });
    },
    onSuccess: () => {
      // FIX batch 5c: the persistent re-add reminder note lived on the connector
      // ROW, which is gone the instant it's revoked — so the user never sees the
      // "also remove it from claude.ai" hint at the moment it matters. Carry it on
      // the success toast (a longer-lived description) so it survives the row
      // removal. Revoking here only kills the Keystone-side credential; claude.ai
      // keeps a dead connector until the user removes it there too.
      toast.success('Claude connector revoked', {
        description:
          'Also remove it from claude.ai: Settings -> Connectors -> disconnect this connector. The Keystone credential is revoked, but claude.ai keeps the connector until you remove it there.',
        duration: 12000,
      });
      setIsConnectorRevokeOpen(false);
      setConnectorToRevoke(null);
    },
  });

  // ── Handlers ──
  function resetCreateForm() {
    setTokenName('');
    setExpiresInDays('90');
    setSelectedTenantId(user?.tenant_id ?? '');
    // AC3: default selection is the Read-only preset, NOT a single hardcoded scope.
    setSelectedScopes(readOnlyPreset(availableScopes));
  }

  // AC3: apply the Read-only preset (every offered :read scope).
  function applyReadOnlyPreset() {
    setSelectedScopes(preset);
  }

  // AC3: per-domain select-all / clear toggle.
  function toggleDomainAll(domain: string, domainScopes: AvailableScope[]) {
    const values = domainScopes.map((s) => s.value);
    const allSelected = values.every((v) => selectedScopes.includes(v));
    setSelectedScopes((prev) =>
      allSelected
        ? prev.filter((s) => !values.includes(s))
        : Array.from(new Set([...prev, ...values]))
    );
  }

  function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!tokenName.trim()) return;
    if (isMultiTenant && !selectedTenantId) {
      toast.error('Please select a tenant');
      return;
    }
    if (selectedScopes.length === 0) {
      toast.error('Select at least one scope');
      return;
    }
    createToken.mutate({
      name: tokenName.trim(),
      expires_days: parseInt(expiresInDays, 10),
      tenant_id: selectedTenantId,
      scopes: selectedScopes,
    });
  }

  function toggleScope(scopeValue: string) {
    setSelectedScopes((prev) =>
      prev.includes(scopeValue)
        ? prev.filter((s) => s !== scopeValue)
        : [...prev, scopeValue]
    );
  }

  function handleRevokeClick(token: ApiToken) {
    setTokenToRevoke(token);
    setIsRevokeOpen(true);
  }

  function handleRevokeConfirm() {
    if (tokenToRevoke) {
      revokeToken.mutate({ id: tokenToRevoke.id, tenant_id: tokenToRevoke.tenant_id });
    }
  }

  // ── Connector handlers (R2) ──

  // R2 AC2: open the connector dialog with the Read-only preset selected by
  // default (the picker offers only role-mintable, no-admin scopes — the SAME
  // available-scopes source as the desktop picker, which is server-filtered).
  function openConnectorDialog() {
    setConnectorScopes(readOnlyPreset(availableScopes));
    setSelectedTenantId(user?.tenant_id ?? '');
    setIsConnectorOpen(true);
  }

  function toggleConnectorScope(scopeValue: string) {
    setConnectorScopes((prev) =>
      prev.includes(scopeValue)
        ? prev.filter((s) => s !== scopeValue)
        : [...prev, scopeValue]
    );
  }

  function toggleConnectorDomainAll(domainScopes: AvailableScope[]) {
    const values = domainScopes.map((s) => s.value);
    const allSelected = values.every((v) => connectorScopes.includes(v));
    setConnectorScopes((prev) =>
      allSelected
        ? prev.filter((s) => !values.includes(s))
        : Array.from(new Set([...prev, ...values]))
    );
  }

  function handleConnectorSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (connectorScopes.length === 0) {
      toast.error('Select at least one scope');
      return;
    }
    // R5 AC1: capture the EXISTING connector's scope set (if any) BEFORE the
    // re-mint so onSuccess can tell whether the scope set changed (and thus
    // whether claude.ai needs a disconnect + re-add).
    const existing = connectorEntries[0];
    priorConnectorScopesRef.current = existing
      ? [...existing.scopes].sort()
      : null;
    // R1 (connector-hardening): the connector mints for the ACTIVE workspace
    // ONLY (one key per tenant) — there is no tenant selector. Always send the
    // current tenant; the backend rejects any other tenant_id by construction.
    mintConnector.mutate({ tenant_id: user?.tenant_id ?? '', scopes: connectorScopes });
  }

  function handleConnectorRevokeClick(token: ApiToken) {
    setConnectorToRevoke(token);
    setIsConnectorRevokeOpen(true);
  }

  function handleConnectorRevokeConfirm() {
    if (connectorToRevoke) {
      revokeConnector.mutate({
        id: connectorToRevoke.id,
        tenant_id: connectorToRevoke.tenant_id,
        owner_uid: connectorToRevoke.owner_uid,
      });
    }
  }

  // R2 AC4/AC8: the connector entries for the active tenant. There is at most one
  // per (uid, tenant) because re-enable REPLACES (the backend overwrites the row);
  // we additionally de-dupe by alias defensively so the UI never shows two.
  const connectorEntries = (() => {
    const seen = new Set<string>();
    const out: ApiToken[] = [];
    for (const t of tokens) {
      if (!t.is_connector_pat) continue;
      const key = t.alias ?? t.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
    }
    return out;
  })();

  // Desktop PATs only — connector entries are surfaced in their own section.
  const desktopTokens = tokens.filter((t) => !t.is_connector_pat);

  // R2 AC7: the expiry the CURRENT connector selection will receive, surfaced in
  // the dialog so the lifetime is not a surprise.
  const connectorExpiryDays = connectorExpiryDaysForScopes(connectorScopes);
  const connectorHasShortExpiry = connectorScopes.some(isDeleteOrActionScope);

  // ── Render ──

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Key className="w-5 h-5 text-gray-500 shrink-0" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900">API Tokens</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Personal Access Tokens for Claude Code and MCP integrations.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href="https://github.com/Build-Gravity/buildgravity-platform/blob/master/services/project-tracker/backend/routers/mcp_README.md"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="connect-claude-code-link"
            className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            Connect to Claude Code
          </a>
          <Button
            onClick={() => {
              resetCreateForm();
              setIsCreateOpen(true);
            }}
            data-testid="create-token-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Token
          </Button>
        </div>
      </div>

      {/* Token list — desktop PATs only. Connector entries (is_connector_pat)
          are listed in their own section below (R2), never twice. */}
      <div className="border rounded-lg divide-y">
        {desktopTokens.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <Key className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No API tokens yet.</p>
            <p className="text-xs mt-1">Create one to connect Claude Code to Keystone.</p>
          </div>
        ) : (
          desktopTokens.map((token) => (
            <TokenRow
              key={token.id}
              token={token}
              isAdmin={isAdmin}
              onRevoke={handleRevokeClick}
            />
          ))
        )}
      </div>

      {/* ── R2: Claude connector (self-serve claude.ai web connector) ──
          Renders ONLY when the user has the ai_connector capability AND the
          tenant has mcp_access (AC1). A non-permitted user sees nothing here. */}
      {connectorAffordanceVisible && (
        <div className="space-y-4" data-testid="connector-section">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-base font-semibold text-gray-900">Claude connector</h3>
              <p className="text-sm text-gray-500 mt-0.5">
                Connect this Keystone workspace to the Claude.ai web app. Picks
                scopes from your own role (no admin) and registers the connection
                for you, no token to paste.
              </p>
            </div>
            <Button
              onClick={openConnectorDialog}
              data-testid="enable-connector-button"
              variant="outline"
            >
              <Plus className="w-4 h-4 mr-2" />
              {connectorEntries.length > 0 ? 'Re-enable Claude connector' : 'Enable Claude connector'}
            </Button>
          </div>

          {/* Connector entries — at most one per tenant (AC4/AC8). */}
          <div className="border rounded-lg divide-y" data-testid="connector-list">
            {connectorEntries.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <p className="text-sm">The Claude connector is not enabled for this workspace.</p>
              </div>
            ) : (
              connectorEntries.map((entry) => {
                // R5 AC2: explicit Expired state + ~T-3d expiry warning (not just
                // an amber past-date badge). An expired connector is silent
                // otherwise — claude.ai caches on a URL that never changes.
                const expired = isExpired(entry.expires_at);
                const expiringSoon =
                  !expired && isExpiringWithinDays(entry.expires_at, CONNECTOR_EXPIRY_WARNING_DAYS);
                const expiring = isExpiringSoon(entry.expires_at);
                return (
                  <div
                    key={entry.id}
                    className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
                    data-testid={`connector-row-${entry.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <ExternalLink className="w-4 h-4 text-blue-600 shrink-0" />
                        <span className="font-medium text-gray-900 truncate">{entry.name}</span>
                        {/* R5 AC2: explicit "Expired" badge — a distinct state,
                            not merely an amber "Expires: <past>" label. */}
                        {expired && (
                          <Badge
                            variant="destructive"
                            className="text-xs px-1.5 py-0"
                            data-testid={`connector-expired-badge-${entry.id}`}
                          >
                            Expired
                          </Badge>
                        )}
                        {isAdmin && entry.user_name && (
                          <span className="text-xs text-gray-400">({entry.user_name})</span>
                        )}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1 items-center">
                        {entry.tenant_name && (
                          <span className="text-xs text-gray-500 mr-1">{entry.tenant_name}</span>
                        )}
                        {entry.scopes.map((s) => (
                          <Badge key={s} variant="secondary" className="text-xs px-1.5 py-0">
                            {s}
                          </Badge>
                        ))}
                      </div>
                      {/* R5 AC2: actionable copy for the expired / soon-to-expire
                          states — re-enable in Keystone + re-add in claude.ai. */}
                      {expired && (
                        <p
                          className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
                          data-testid={`connector-expired-note-${entry.id}`}
                        >
                          This connector has expired. Re-enable it here, then disconnect
                          and re-add it in claude.ai (Settings → Connectors).
                        </p>
                      )}
                      {expiringSoon && (
                        <p
                          className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                          data-testid={`connector-expiry-warning-${entry.id}`}
                        >
                          This connector expires soon ({formatDate(entry.expires_at)}).
                          Re-enable it to avoid an interruption — you will then need to
                          disconnect and re-add it in claude.ai.
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col sm:items-end text-xs text-gray-400 gap-0.5 shrink-0">
                      <span
                        className={
                          expired
                            ? 'text-red-600 font-medium'
                            : expiring
                            ? 'text-amber-600 font-medium'
                            : ''
                        }
                      >
                        {expired ? 'Expired: ' : 'Expires: '}
                        {formatDate(entry.expires_at)}
                        {!expired && expiring && ' ⚠'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleConnectorRevokeClick(entry)}
                      data-testid={`revoke-connector-${entry.id}`}
                      className="text-gray-400 hover:text-red-600 shrink-0"
                      aria-label={`Revoke Claude connector ${entry.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>

          {/* R5 AC1: persistent reminder on the connector surface — any scope
              change, re-enable, or revoke requires a disconnect + re-add in
              claude.ai because claude.ai caches the tool catalog on a URL that
              never changes. Shown whenever a connector entry exists. */}
          {connectorEntries.length > 0 && (
            <p
              className="flex items-start gap-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-md px-3 py-2"
              data-testid="connector-readd-note"
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                Changed scopes, re-enabled, or revoked this connector? You must
                disconnect and re-add it in claude.ai (Settings → Connectors) for
                the change to take effect — claude.ai caches the connector on a URL
                that does not change.
              </span>
            </p>
          )}
        </div>
      )}

      {/* R5 AC4: half-configured discoverability. When the workspace has
          mcp_access but THIS user lacks the ai_connector capability, the whole
          connector affordance is hidden — which looked like the feature simply
          doesn't exist. Surface a CTA so the user knows to ask an admin. */}
      {!connectorAffordanceVisible && tenantHasMcpAccess && !hasAiConnectorCapability && (
        <div
          className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
          data-testid="connector-half-config-cta"
        >
          <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            The Claude connector is available for this workspace — ask an admin to
            enable it for your account.
          </span>
        </div>
      )}

      {/* R2: connector enable dialog (scope picker + expiry surfacing) */}
      <Dialog open={isConnectorOpen} onOpenChange={setIsConnectorOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Enable Claude connector</DialogTitle>
            <DialogDescription>
              Choose what Claude can do in{' '}
              <strong>{user?.tenant?.tenant_name ?? user?.tenant_id}</strong>, your
              current workspace. The connector is always scoped to the workspace
              you have open — to set one up for a different workspace, switch to it
              first. Scopes are limited to your own role, with no admin access over
              the web connector.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleConnectorSubmit} className="space-y-5 mt-2">
            {/* R1 (connector-hardening): NO tenant selector. The connector mints
                for the ACTIVE workspace only (one key per tenant); the active
                tenant is the SAME membership the ai_connector gate is evaluated
                against, so the inner gate and the minted tenant can never
                diverge. To mint for another workspace, switch to it first. */}

            {/* Scopes — role-mintable only, default read-only (AC2) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Scopes</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConnectorScopes(preset)}
                  disabled={preset.length === 0}
                  data-testid="connector-scope-preset-read-only"
                  className="h-7 text-xs"
                >
                  Read-only preset
                </Button>
              </div>
              {availableScopes.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="connector-no-mintable-scopes">
                  No scopes are available for your role.
                </p>
              ) : (
                <div className="space-y-4 max-h-[48vh] overflow-y-auto pr-1">
                  {scopeGroups.map(([domain, domainScopes]) => {
                    const values = domainScopes.map((s) => s.value);
                    const allSelected = values.every((v) => connectorScopes.includes(v));
                    return (
                      <div
                        key={domain}
                        className="border rounded-md p-3 space-y-2"
                        data-testid={`connector-scope-group-${domain}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">
                            {domainLabel(domain)}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleConnectorDomainAll(domainScopes)}
                            data-testid={`connector-scope-select-all-${domain}`}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {allSelected ? 'Clear all' : 'Select all'}
                          </button>
                        </div>
                        <div className="space-y-2 pl-1">
                          {domainScopes.map((scope) => {
                            const checked = connectorScopes.includes(scope.value);
                            return (
                              <div key={scope.value} className="flex items-start gap-2.5">
                                <Checkbox
                                  id={`connector-scope-${scope.value}`}
                                  data-testid={`connector-scope-checkbox-${scope.value}`}
                                  checked={checked}
                                  onCheckedChange={() => toggleConnectorScope(scope.value)}
                                  className="mt-0.5"
                                />
                                <div>
                                  <label
                                    htmlFor={`connector-scope-${scope.value}`}
                                    className="text-sm font-medium cursor-pointer text-gray-800"
                                  >
                                    {scope.label}
                                  </label>
                                  <p className="text-xs text-gray-500">{scope.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* R2 AC7: surface the expiry the selection earns — short for
                :delete / action scopes, 90-day default otherwise. */}
            <div
              className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm border ${
                connectorHasShortExpiry
                  ? 'text-amber-800 bg-amber-50 border-amber-200'
                  : 'text-gray-600 bg-gray-50 border-gray-200'
              }`}
              data-testid="connector-expiry-notice"
            >
              {connectorHasShortExpiry && <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
              <span data-testid="connector-expiry-text">
                {connectorHasShortExpiry ? (
                  <>
                    This connection includes a <strong>delete or action</strong> scope,
                    so it will expire in <strong>{connectorExpiryDays} days</strong> (a
                    shorter, safer window). Read-only and write-only connections last{' '}
                    {CONNECTOR_DEFAULT_EXPIRY_DAYS} days.
                  </>
                ) : (
                  <>This connection will last <strong>{connectorExpiryDays} days</strong>.</>
                )}
              </span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsConnectorOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={mintConnector.isPending || connectorScopes.length === 0}
                data-testid="connector-submit"
              >
                {mintConnector.isPending ? 'Enabling…' : 'Enable connector'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* R2 AC3: connector reveal — the WEB connector URL for claude.ai. */}
      <Dialog
        open={revealedConnectorUrl !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevealedConnectorUrl(null);
            setRevealReAddRequired(false);
          }
        }}
      >
        <DialogContent className="sm:max-w-[540px]">
          <DialogHeader>
            <DialogTitle>Claude connector enabled</DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2 mt-2">
                <Check className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium" data-testid="connector-registered-confirmation">
                  This workspace is now registered for the Claude connector.
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>
          {/* R5 AC1: a scope change does NOT reach claude.ai on its own — its tool
              catalog is cached on a URL that never changes. Make the required
              disconnect + re-add prominent when the re-mint changed the scopes. */}
          {revealReAddRequired && (
            <div
              className="mt-3 flex items-start gap-2 text-amber-900 bg-amber-50 border border-amber-300 rounded-md px-3 py-2"
              data-testid="connector-readd-callout"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span className="text-sm font-medium">
                You changed this connector's scopes. You must disconnect and re-add
                this connector in claude.ai (Settings → Connectors) for the change
                to take effect — claude.ai caches the tool catalog on a URL that
                does not change.
              </span>
            </div>
          )}
          <div className="mt-3 space-y-3">
            <p className="text-sm text-gray-600">
              In Claude.ai, open <strong>Settings → Connectors → Add custom connector</strong> and
              paste this URL:
            </p>
            {revealedConnectorUrl ? (
              <div className="flex items-center gap-2">
                <code
                  className="flex-1 text-xs font-mono bg-gray-100 border rounded px-3 py-2 break-all select-all"
                  data-testid="connector-web-url"
                >
                  {revealedConnectorUrl}
                </code>
                <CopyButton text={revealedConnectorUrl} />
              </div>
            ) : (
              <p
                className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                data-testid="connector-url-unavailable"
              >
                The connector is registered, but the connector URL is not configured
                for this environment. Contact your administrator for the claude.ai
                connector URL.
              </p>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button onClick={() => setRevealedConnectorUrl(null)} data-testid="connector-reveal-dismiss">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* R2 AC4: connector revoke confirmation. */}
      <AlertDialog open={isConnectorRevokeOpen} onOpenChange={setIsConnectorRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Claude connector</AlertDialogTitle>
            <AlertDialogDescription data-testid="connector-revoke-description">
              Disconnect <strong>{connectorToRevoke?.name}</strong>? Claude.ai will
              immediately lose access to this workspace and the registration will be
              removed. You should also remove this connector from claude.ai
              (Settings → Connectors) — it will otherwise keep showing as connected
              because claude.ai caches it on a URL that does not change.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConnectorRevokeConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-revoke-connector-button"
            >
              Revoke connector
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create token dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Create API Token</DialogTitle>
            <DialogDescription>
              The full token is shown once after creation. Store it securely.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateSubmit} className="space-y-5 mt-2">
            {/* Name */}
            <div className="space-y-1.5">
              <Label htmlFor="token-name">Token name</Label>
              <Input
                id="token-name"
                data-testid="token-name-input"
                placeholder="e.g. Claude Code - Jamie"
                value={tokenName}
                onChange={(e) => setTokenName(e.target.value)}
                required
              />
            </div>

            {/* Expiration */}
            <div className="space-y-1.5">
              <Label htmlFor="token-expiry">Expiration</Label>
              <Select value={expiresInDays} onValueChange={setExpiresInDays}>
                <SelectTrigger id="token-expiry" data-testid="expiry-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {EXPIRATION_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Tenant selector */}
            <div className="space-y-1.5">
              <Label htmlFor="token-tenant">Tenant</Label>
              {isMultiTenant ? (
                <Select
                  value={selectedTenantId}
                  onValueChange={setSelectedTenantId}
                >
                  <SelectTrigger id="token-tenant" data-testid="tenant-select">
                    <SelectValue placeholder="Select tenant…" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTenants.map((t) => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div
                  className="px-3 py-2 bg-gray-50 border rounded-md text-sm text-gray-700"
                  data-testid="tenant-display"
                >
                  {availableTenants[0]?.name ?? user?.tenant_id}
                </div>
              )}
            </div>

            {/* Scopes — rendered from available-scopes, grouped by domain (AC3) */}
            <div className="space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <Label>Scopes</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={applyReadOnlyPreset}
                  disabled={preset.length === 0}
                  data-testid="scope-preset-read-only"
                  className="h-7 text-xs"
                >
                  Read-only preset
                </Button>
              </div>
              {availableScopes.length === 0 ? (
                <p className="text-sm text-gray-500" data-testid="no-mintable-scopes">
                  No scopes are available for your role.
                </p>
              ) : (
                <div className="space-y-4 max-h-[48vh] overflow-y-auto pr-1">
                  {scopeGroups.map(([domain, domainScopes]) => {
                    const values = domainScopes.map((s) => s.value);
                    const allSelected = values.every((v) => selectedScopes.includes(v));
                    return (
                      <div
                        key={domain}
                        className="border rounded-md p-3 space-y-2"
                        data-testid={`scope-group-${domain}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-gray-700">
                            {domainLabel(domain)}
                          </span>
                          <button
                            type="button"
                            onClick={() => toggleDomainAll(domain, domainScopes)}
                            data-testid={`scope-select-all-${domain}`}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {allSelected ? 'Clear all' : 'Select all'}
                          </button>
                        </div>
                        <div className="space-y-2 pl-1">
                          {domainScopes.map((scope) => {
                            const checked = selectedScopes.includes(scope.value);
                            return (
                              <div key={scope.value} className="flex items-start gap-2.5">
                                <Checkbox
                                  id={`scope-${scope.value}`}
                                  data-testid={`scope-checkbox-${scope.value}`}
                                  checked={checked}
                                  onCheckedChange={() => toggleScope(scope.value)}
                                  className="mt-0.5"
                                />
                                <div>
                                  <label
                                    htmlFor={`scope-${scope.value}`}
                                    className="text-sm font-medium cursor-pointer text-gray-800"
                                  >
                                    {scope.label}
                                  </label>
                                  <p className="text-xs text-gray-500">{scope.description}</p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createToken.isPending || !tokenName.trim() || selectedScopes.length === 0}
                data-testid="create-token-submit"
              >
                {createToken.isPending ? 'Creating…' : 'Create Token'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Token reveal dialog (shown once) */}
      <Dialog
        open={!!revealedToken}
        onOpenChange={(open) => {
          if (!open) {
            setRevealedToken(null);
            setRevealedScopes([]);
          }
        }}
      >
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Token Created</DialogTitle>
            <DialogDescription asChild>
              <div className="flex items-center gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span className="text-sm font-medium">
                  Copy this token now. It will never be shown again.
                </span>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 space-y-3">
            <div className="flex items-center gap-2">
              <code
                className="flex-1 text-xs font-mono bg-gray-100 border rounded px-3 py-2 break-all select-all"
                data-testid="revealed-token"
              >
                {revealedToken}
              </code>
              {revealedToken && <CopyButton text={revealedToken} />}
            </div>
            {tierForScopes(revealedScopes) === 'super-admin' ? (
              // AC4 (S7): a super-admin-tier PAT has NO web connector in W1b — the
              // admin tools live behind the desktop shim (W3). Show the notice and
              // DO NOT render a --transport http command (there is nothing to
              // connect to over the web for an admin-tier token).
              <div
                className="flex items-start gap-2 text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2"
                data-testid="admin-tier-connect-notice"
              >
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="text-sm">
                  This is a super-admin-tier token: <strong>desktop-shim only;
                  web connector unavailable</strong>. Admin tools are not exposed
                  over the web transport. Use it with the Keystone desktop shim
                  (shipping with W3) — there is no <code>claude mcp add</code> web
                  command for an admin-tier token.
                </span>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  Use this token in Claude Code:
                </p>
                <code
                  className="block text-xs font-mono bg-gray-100 border rounded px-3 py-2 break-all"
                  data-testid="connect-command"
                >
                  {`claude mcp add --transport http keystone <api-url>/mcp --header "Authorization: Bearer ${revealedToken ?? ''}"`}
                </code>
              </>
            )}
          </div>
          <DialogFooter className="mt-4">
            <Button
              onClick={() => {
                setRevealedToken(null);
                setRevealedScopes([]);
              }}
              data-testid="token-reveal-dismiss"
            >
              I've saved my token
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke confirmation */}
      <AlertDialog open={isRevokeOpen} onOpenChange={setIsRevokeOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Token</AlertDialogTitle>
            <AlertDialogDescription>
              Revoke <strong>{tokenToRevoke?.name}</strong>? Any integrations using this token
              will stop working immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevokeConfirm}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-revoke-button"
            >
              Revoke Token
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
